import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useCallback, useContext, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import * as z from "zod";
import {
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { Form } from "../../../../../layout/FormLayout";
import { PageWrapper, PageTitle } from "../../../../../layout/portalPageLayout";
import { endpoints } from "../../../../../endpoints";
import type { OptionType, UUID } from "../../../../../types/types";
import type { AttributeTemplateSummarySchema } from "../../../../../types/types.PIM";
import type { EntityProfile, EntityType } from "../../../../../types/types.MDM";
import {
  isAxiosError,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { zodSelectBoxDefault } from "../../../../../util/zod.util";
import { strings } from "../../../../../util/strings";
import { stringIsNotOnlyWhiteSpaceRegex } from "../../../../../util/regexes";
import { useRoutePath } from "../../../../../util/Routing";
import { TextXlSemibold } from "../../../../../components/Typography/TypographyV2";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 537px;
  padding: 15px ${({ theme }) => theme.resolve("spacing_none")} 15px;
  margin: 15px ${({ theme }) => theme.resolve("spacing_none")};
  & > * {
    margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
`;

type FormOutput = {
  name: string;
  template: OptionType<UUID>;
};

function getEntityListEndpoint(
  entityType: EntityType,
  tenantId: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers(tenantId);
    case "supplier":
      return endpoints.v2_tenants_id_suppliers(tenantId);
    case "distributor":
      return endpoints.v2_tenants_id_distributors(tenantId);
  }
}

type CreateEntityPageProps = {
  entityType: EntityType;
  onCancel?: () => void;
};

export function EntityCreationForm({
  entityType,
  onCancel,
}: CreateEntityPageProps): JSX.Element {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const { getLabel, getTemplateDisplayName } = useMdmEntityDisplayNames();
  const entityLabel = getLabel(entityType, "singular");
  const detailBasePath = `${adminPath}/mdm/${entityType}s`;
  const listPath = detailBasePath;

  const templateParams = useMemo(
    () => new URLSearchParams({ order_by: "asc", template_type: entityType }),
    [entityType]
  );

  const getTemplateOptions = useCallback(
    (response: AttributeTemplateSummarySchema[]): OptionType<UUID>[] =>
      response.map((template) => ({
        value: template.template_id,
        label: getTemplateDisplayName(template),
      })),
    [getTemplateDisplayName]
  );

  const { register, handleSubmit, control, formState, errors } = useFormWrapper(
    {
      resolver: zodResolver(
        z.object({
          name: z
            .string()
            .regex(
              stringIsNotOnlyWhiteSpaceRegex,
              strings(t).thisIsARequiredField
            ),
          template: zodSelectBoxDefault(t),
        })
      ),
    }
  );

  const onSubmit = async (values: FormOutput) => {
    try {
      const endpoint = getEntityListEndpoint(entityType, tenant_id);
      const { data: entity } = await axios.post<EntityProfile>(endpoint, {
        name: values.name.trim(),
        entity_type: entityType,
        template_id: values.template.value,
      });
      notifySuccess(
        t("{{label}} created successfully", { label: entityLabel })
      );
      history.push(`${detailBasePath}/${entity.id}`);
    } catch (error) {
      if (isAxiosError(error)) {
        notifyError(
          t("A {{label}} with that name already exists", {
            label: entityLabel.toLowerCase(),
          }),
          { error }
        );
      }
    }
  };

  return (
    <FormContainer>
      <TextXlSemibold>
        {t("{{label}} Details", { label: entityLabel })}
      </TextXlSemibold>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name="name"
          label={t("{{label}} Name", { label: entityLabel })}
          theref={register({ required: true })}
          formState={formState}
          errors={errors}
          type="text"
        />
        <Controller
          as={SearchSelectInfiniteScroll}
          control={control}
          name="template"
          label={t("Template")}
          placeholder={t("Select a template")}
          baseUrl={endpoints.v2_storefronts_id_pim_templates_summary(tenant_id)}
          params={templateParams}
          getOptions={getTemplateOptions}
          defaultValue={{ value: "", label: "" }}
          errors={errors}
          formState={formState}
          menuPosition={"fixed"}
        />
        <ButtonContainer>
          <TertiaryButtonMediumV2
            type="button"
            onClick={() => (onCancel ? onCancel() : history.push(listPath))}
          >
            {t("Cancel")}
          </TertiaryButtonMediumV2>
          <PrimaryButtonMediumV2 type="submit">
            {t("Create")}
          </PrimaryButtonMediumV2>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
}

export function CreateEntityPage({
  entityType,
}: CreateEntityPageProps): JSX.Element {
  const { t } = useTranslation();
  const { getLabel } = useMdmEntityDisplayNames();
  const entityLabel = getLabel(entityType, "singular");

  return (
    <PageWrapper>
      <PageTitle>{t("Create {{label}}", { label: entityLabel })}</PageTitle>
      <EntityCreationForm entityType={entityType} />
    </PageWrapper>
  );
}
